import React from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { ClientHelper } from '../../client/CreateNswagClient';
import { useLoginContext } from '../login/LoginHandler';

type PeoplePickerProps = {
  label: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
};

export function PeoplePicker({
  label,
  onChange,
  disabled = false,
  inputRef: externalInputRef,
}: PeoplePickerProps) {
  const internalInputRef = React.useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const consumer = useLoginContext();
  const [value, setValue] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<string[]>([]);
  const [resolving, setResolving] = React.useState(false);
  const canResolve = value.trim().length > 0 && !resolving && !disabled;

  const clearSuggestions = React.useCallback(() => {
    setSuggestions([]);
  }, []);

  const handlePick = React.useCallback((picked: string) => {
    if (!picked) return;
    onChange(picked);
    setValue('');
    clearSuggestions();
  }, [clearSuggestions, onChange]);

  const resolveNow = React.useCallback(async () => {
    if (!canResolve) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (trimmed.includes('@')) {
      handlePick(trimmed);
      return;
    }
    setResolving(true);
    try {
      const token = await consumer.getUserToken();
      if (!token) {
        setSuggestions([]);
        return;
      }
      const usersClient = ClientHelper.createUsersClient(token);
      const resolved = await usersClient.resolve(trimmed);
      if (resolved.length === 0) {
        setSuggestions([]);
      } else if (resolved.length === 1) {
        handlePick(resolved[0]);
      } else {
        setSuggestions(resolved);
      }
    } finally {
      setResolving(false);
    }
  }, [canResolve, consumer, handlePick, value]);

  return (
    <Stack spacing={1.5}>
      <TextField
        label={label}
        value={value}
        onChange={(ev) => setValue(ev.target.value)}
        inputRef={inputRef}
        inputProps={{
          onKeyDown: (ev: React.KeyboardEvent<HTMLInputElement>) => {
            if (ev.key !== 'Enter') return;
            ev.preventDefault();
            ev.stopPropagation();
            resolveNow();
          },
        }}
        fullWidth
        disabled={disabled}
      />
      <Box display="flex" gap={1}>
        <Button
          variant="outlined"
          disabled={!canResolve}
          onClick={resolveNow}
        >
          {resolving ? 'Löse auf...' : 'Auflösen / Hinzufügen'}
        </Button>
        <Button
          variant="text"
          disabled={!value.trim() || value.includes('@') || disabled}
          onClick={clearSuggestions}
        >
          Treffer löschen
        </Button>
      </Box>
      {suggestions.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Vorschläge:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {suggestions.map((item) => (
              <Chip
                key={item}
                label={item}
                onClick={() => {
                  handlePick(item);
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
